import { useEffect, useState } from 'react';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { IconList } from '@components/icons/list';
import { IconPin } from '@components/icons/pin';
import { RelativeSpinner } from '@components/loading-spinner';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useIsMobile } from '@hooks/use-is-mobile';

import {
  useDepartmentOrderingList,
  usePinDepartment,
  useUnPinDepartment,
} from 'api/departments/hooks';
import { DepartmentOrderingData } from 'api/departments/requests';
import { useStore } from 'models/Provider';

import { DepartmentCard, DraggableDepartment } from './draggable-department';
import { DraggingState, EmptyCard } from './ordering';

export const OrderDepartments = () => {
  const departmentQuery = useDepartmentOrderingList();

  if (!departmentQuery.isSuccess) {
    return <RelativeSpinner />;
  }
  return <RenderDepartments departments={departmentQuery.data} />;
};

export const RenderDepartments = (props: { departments: DepartmentOrderingData[] }) => {
  const { departments } = props;
  const { t } = useStore();
  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);
  const [pinnedDepartments, setPinnedDepartments] = useState<DepartmentOrderingData[]>(
    departments.filter((department) => department.pinned),
  );

  useEffect(() => {
    setPinnedDepartments(departments.filter((department) => department.pinned === true));
  }, [departments]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  const isMobile = useIsMobile();
  const pin = usePinDepartment();
  const unpin = useUnPinDepartment();

  const departmentIds = departments.map((department) => String(department.id));
  const hasPinnedDepartments = pinnedDepartments.length > 0;
  const unPinnedDepartments = departments.filter((department) => !department.pinned);
  const hasUnpinnedDepartments = unPinnedDepartments.length > 0;

  const draggingDepartment = draggingItem ? departments[draggingItem.index] : null;

  const handlePinDepartment = (departmentId: number) => {
    let order = 1;
    if (pinnedDepartments.length > 0) {
      const pinnedDepartmentsOrder = pinnedDepartments.map((department) => department.order);
      order += Math.max(...pinnedDepartmentsOrder);
    }
    pin.mutate({ departmentId: departmentId, ordering: order });
  };

  function onDragStart(event: DragStartEvent) {
    if (isMobile) return;
    const { active } = event;
    const order = active.data.current?.order;
    setDraggingItem({
      order: order,
      index: active.data.current?.sortable.index,
      id: active.data.current?.id,
    });
  }

  async function onDragEnd(event: DragEndEvent) {
    if (!draggingItem) return;
    const { active, over } = event;
    setDraggingItem(null);
    if (!over || active.id === over.id) return;

    const newOrder = over.data.current?.order;
    const departmentId = active.id;
    setPinnedDepartments((items) => {
      const activeIndex = items.findIndex((item) => String(item.id) === active.id);
      const overIndex = items.findIndex((item) => String(item.id) === over.id);
      return arrayMove(items, activeIndex, overIndex);
    });
    pin.mutate({ departmentId: +departmentId, ordering: newOrder });
  }

  function onDragCancel() {
    setDraggingItem(null);
  }

  return (
    <>
      <Stack space={2}>
        <Stack space={2} orientation="horizontal">
          <Text font="semibold14" color="purple100" text={t('company.settings.joborder.pinned')} />
          <Text font="regular14" color="purple50" text={t('company.settings.joborder.manually')} />
        </Stack>
        {hasPinnedDepartments ? (
          <DndContext
            sensors={sensors}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
          >
            <DragOverlay>
              {draggingDepartment && <DepartmentCard department={draggingDepartment} />}
            </DragOverlay>
            <SortableContext items={departmentIds} strategy={verticalListSortingStrategy}>
              {pinnedDepartments.map((department) => (
                <Stack space={2} key={department.id}>
                  <DraggableDepartment
                    department={department}
                    unpin={unpin.mutate}
                    isPinned
                    isDragging={draggingDepartment?.id === department.id}
                  />
                </Stack>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <EmptyCard
            text={t('company.settings.joborder.departments.pinned.none')}
            icon={<IconPin color="purple60" />}
          />
        )}
      </Stack>
      <Separator />
      <Stack space={2}>
        <Stack space={2} orientation="horizontal">
          <Text
            font="semibold14"
            color="purple100"
            text={t('company.settings.joborder.departments.unpinned')}
          />
          <Text
            font="regular14"
            color="purple50"
            text={t('company.settings.joborder.departments.unpinned.order')}
          />
        </Stack>
        {hasUnpinnedDepartments ? (
          <>
            {unPinnedDepartments.map((department) => (
              <Stack space={2} key={department.id}>
                <DraggableDepartment
                  department={department}
                  unpin={unpin.mutate}
                  handlePinDepartment={handlePinDepartment}
                />
              </Stack>
            ))}
          </>
        ) : (
          <EmptyCard
            text={t('company.settings.joborder.departments.unpinned.none')}
            icon={<IconList color="purple80" />}
          />
        )}
      </Stack>
    </>
  );
};
